import type { EmailPagesParams } from "@common/api/emails"
import {
  periodRange,
  periodSchema,
} from "@common/components/filters/period-model"
import { z } from "zod"

export const TABS = ["private", "team", "archived"] as const

const tabSchema = z.enum(TABS)

export type Tab = z.infer<typeof tabSchema>

const VISIBILITY_KEY = "fleetcraft.emails.visibility"

export const emailsSearchSchema = z.object({
  tab: tabSchema.optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  sender: z.array(z.string()).optional().catch(undefined),
  period: periodSchema.optional().catch(undefined),
  from: z.string().optional().catch(undefined),
  to: z.string().optional().catch(undefined),
})

export type EmailsSearch = z.infer<typeof emailsSearchSchema>

export const bulkIdsSearchSchema = z.object({
  ids: z.preprocess(
    (value) =>
      Array.isArray(value) ? value : value === undefined ? undefined : [value],
    z.array(z.string().uuid()).catch([]),
  ),
})

export function tabLabel(tab: Tab): string {
  switch (tab) {
    case "private":
      return "Private"
    case "team":
      return "Team"
    case "archived":
      return "Archived"
  }
}

export function tabOf(search: EmailsSearch): Tab {
  return search.tab ?? rememberedTab()
}

function rememberedTab(): Tab {
  if (typeof window === "undefined") return "private"
  const visibility = window.localStorage.getItem(VISIBILITY_KEY)
  return visibility === "tenant" ? "team" : "private"
}

export function rememberTab(tab: Tab): void {
  const { visibility } = tabParams(tab)
  if (visibility) window.localStorage.setItem(VISIBILITY_KEY, visibility)
}

export function tabParams(
  tab: Tab,
): Pick<EmailPagesParams, "archived" | "visibility"> {
  switch (tab) {
    case "private":
      return { archived: false, visibility: "user" }
    case "team":
      return { archived: false, visibility: "tenant" }
    case "archived":
      return { archived: true, visibility: null }
  }
}

export function emailPagesParams(search: EmailsSearch): EmailPagesParams {
  const range = periodRange(search.period, search.from, search.to)
  return {
    ...tabParams(tabOf(search)),
    search: search.search || null,
    from_address: search.sender?.length ? search.sender : null,
    received_from: range.from,
    received_to: range.to,
  }
}
